import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../utils/normLabels'
import { formatTitle } from '../../utils/formatTitle'
import { splitFamilyLabel } from '../../utils/familyLabel'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { resetSecurityCheckFamily } from '../../redux/securityCheckFamily/securityCheckFamilySlice'
import { fetchSecurityCheckFamilyByCategory } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import TablePager from '../../components/common/TablePager'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { useClientPagination } from '../../hooks/useClientPagination'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import i18n from '../../i18n'
import {
  ListPageShell,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { onClickableTableRow } from '../../utils/tableRowClick'
import MappingFlowStepper from './MappingFlowStepper'
import '../../components/common/ListPageTable.css'
import '../securityFramework/SecurityFramework.css'
import './MappingFlow.css'
import { formatDecimal } from '../../utils/formatNumbers'

function SecurityCheckFamilyToMap() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const [search, setSearch] = useState('')

  const dispatch: AppDispatch = useAppDispatch()
  const { SecurityCheckFamilyResponse, loading } = useSelector(
    (state: RootState) => state.securitycheckFamily
  )
  const showLoader = useTableLoadingGate(loading, [id, i18n.language])

  useEffect(() => {
    dispatch(resetSecurityCheckFamily())
    const action = fetchSecurityCheckFamilyByCategory({
      category: id ? id : '',
      page: 1,
      pageSize: 200,
      language: i18n.language.toLocaleUpperCase(),
    })

    if (action) {
      dispatch(action)
    }
  }, [dispatch, id, i18n.language])

  const families = SecurityCheckFamilyResponse?.securityCheckFamilies || []
  const filteredFamilies = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return families
    return families.filter((family: any) =>
      [family.LabelSCF, family.Source]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    )
  }, [families, search])

  const pager = useClientPagination(filteredFamilies, 10)
  const sourceFramework = formatNormLabel(id || '')
  const targetFramework = formatNormLabel(selectedNorm)
  const familiesTotal =
    SecurityCheckFamilyResponse?.total ?? families.length

  const openFamily = (familyId: string) => {
    navigate(`/securityControlsToMap/${id}/${familyId}`)
  }

  return (
    <div className="mapping-flow">
      <MappingFlowStepper currentStep={2} sourceNorm={id} />

      <ListPageShell
        eyebrow={t('frameworkMappingPage.familiesEyebrow')}
        title={t('frameworkMappingPage.familiesTitle', {
          source: sourceFramework,
          target: targetFramework,
        })}
        subtitle={t('frameworkMappingPage.familiesSubtitle', {
          source: sourceFramework,
          target: targetFramework,
        })}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('frameworkMappingPage.filterFamilies')}
        countLabel={t('frameworkMappingPage.familiesCount', {
          count: filteredFamilies.length,
        })}
        showFiltersChip={false}
      >
        <div className="mapping-flow-shell-body">
          <div className="fw-source-bar mapping-flow-context">
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.sourceFramework')}
              </div>
              <div className="fw-stat-value">{sourceFramework}</div>
            </div>
            <div className="fw-stat-divider" aria-hidden />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.targetFramework')}
              </div>
              <div className="fw-stat-value">{targetFramework}</div>
            </div>
            <div className="fw-stat-divider" aria-hidden />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.familiesLoaded')}
              </div>
              <div className="fw-stat-value fw-mono">{familiesTotal}</div>
            </div>
            <button
              type="button"
              className="fw-ghost-btn"
              onClick={() => navigate('/securityFramework')}
            >
              {t('frameworkMappingPage.backToTargets')}
            </button>
          </div>

          <div className="incyia-table-wrap">
            {showLoader ? (
              <TableLoadingState />
            ) : filteredFamilies.length === 0 ? (
              <TableEmptyState />
            ) : (
              <>
                <table className="incyia-table incyia-table--families">
                  <thead>
                    <tr>
                      <th>{t('Label')}</th>
                      <th>{t('Source')}</th>
                      <th>{t('listPages.score')}</th>
                      <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pager.pageItems.map((family: any) => {
                      const familyId = family._id as string
                      const score =
                        family.SCFScore != null &&
                        !Number.isNaN(Number(family.SCFScore))
                          ? formatDecimal(Number(family.SCFScore))
                          : '—'

                      const parsed = splitFamilyLabel(
                        family.LabelSCF || '',
                        family.Source || ''
                      )

                      return (
                        <tr key={familyId} onClick={onClickableTableRow}>
                          <td className="incyia-col-pinned">
                            <button
                              type="button"
                              className="incyia-family-label"
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                width: '100%',
                              }}
                              onClick={() => openFamily(familyId)}
                            >
                              <span className="incyia-family-code">
                                {parsed.code}
                              </span>
                              {parsed.full ? (
                                <span
                                  className="incyia-family-name"
                                  title={formatTitle(parsed.full)}
                                >
                                  {formatTitle(parsed.full)}
                                </span>
                              ) : null}
                            </button>
                          </td>
                          <td className="incyia-muted">
                            <span className="incyia-family-source">
                              {parsed.source || '—'}
                            </span>
                          </td>
                          <td>
                            <span className="incyia-score">{score}</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="incyia-row-actions">
                              <button
                                type="button"
                                className="mapping-flow-row-cta"
                                title={t('frameworkMappingPage.continueToControls')}
                                onClick={() => openFamily(familyId)}
                                style={listPageIconBtnStyle}
                              >
                                <span className="mapping-flow-row-cta-label">
                                  {t('frameworkMappingPage.continueShort')}
                                </span>
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {pager.showPager ? (
                  <TablePager
                    page={pager.page}
                    pageSize={pager.pageSize}
                    total={pager.total}
                    pageCount={pager.pageCount}
                    from={pager.from}
                    to={pager.to}
                    pageSizeOptions={pager.pageSizeOptions}
                    onPageChange={pager.setPage}
                    onPageSizeChange={pager.setPageSize}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </ListPageShell>
    </div>
  )
}

export default SecurityCheckFamilyToMap
